/**
 * Workflow Validation System
 * Main orchestrator for client-side workflow validation
 */

import { Edge } from '@xyflow/react'
import { WorkflowNode } from '@store/workflowStore'
import {
  WorkflowValidationResult,
  ValidationIssue,
  ValidationSeverity,
  NodeValidationState,
} from '@features/adventures/types/validation.types'
import { validateConnectivity } from './connectivity.validator'
import { validateChallengeNodes } from './challenge.validator'
import { validateFlowNodes } from './flow.validator'
import { validateRewardNodes } from './reward.validator'
import { analyzeGraph } from './graph.validator'
import { resetIssueCounter } from './helpers'

export interface ValidateWorkflowParams {
  nodes: WorkflowNode[]
  edges: Edge[]
}

/**
 * Build per-node validation state map for UI highlighting
 */
function buildNodeStateMap(
  issues: ValidationIssue[],
  nodes: WorkflowNode[]
): Map<string, NodeValidationState> {
  const stateMap = new Map<string, NodeValidationState>()

  // Initialize all nodes with clean state
  for (const node of nodes) {
    stateMap.set(node.id, {
      nodeId: node.id,
      hasErrors: false,
      hasWarnings: false,
      errorCount: 0,
      warningCount: 0,
      issues: [],
    })
  }

  // Populate issues per node
  for (const issue of issues) {
    if (!issue.nodeId) continue

    const state = stateMap.get(issue.nodeId)
    if (!state) continue

    state.issues.push(issue)
    if (issue.severity === ValidationSeverity.Error) {
      state.hasErrors = true
      state.errorCount++
    } else if (issue.severity === ValidationSeverity.Warning) {
      state.hasWarnings = true
      state.warningCount++
    }
  }

  return stateMap
}

/**
 * Main validation orchestrator
 * Runs all validation passes and aggregates results
 */
export function validateWorkflow({ nodes, edges }: ValidateWorkflowParams): WorkflowValidationResult {
  // Reset issue counter for consistent IDs
  resetIssueCounter()

  const allIssues: ValidationIssue[] = []

  // Phase 1: Connectivity validation
  allIssues.push(...validateConnectivity(nodes, edges))

  // Phase 2: Node configuration validation
  allIssues.push(...validateChallengeNodes(nodes))
  allIssues.push(...validateFlowNodes(nodes))
  allIssues.push(...validateRewardNodes(nodes))

  // Phase 3: Graph structure analysis
  const graphAnalysis = analyzeGraph(nodes, edges)
  allIssues.push(...graphAnalysis.issues)

  // Separate errors and warnings
  const errors = allIssues.filter(i => i.severity === ValidationSeverity.Error)
  const warnings = allIssues.filter(i => i.severity === ValidationSeverity.Warning)

  // Build per-node state map
  const nodeStates = buildNodeStateMap(allIssues, nodes)

  return {
    isValid: errors.length === 0,
    timestamp: new Date(),
    errorCount: errors.length,
    warningCount: warnings.length,
    errors,
    warnings,
    nodeStates,
    graphAnalysis: {
      hasUnreachableNodes: graphAnalysis.hasUnreachableNodes,
      unreachableNodeIds: graphAnalysis.unreachableNodeIds,
      hasCycles: graphAnalysis.hasCycles,
      cycleNodeIds: graphAnalysis.cycleNodeIds,
      hasOrphanedPaths: graphAnalysis.hasOrphanedPaths,
      orphanedPathNodeIds: graphAnalysis.orphanedPathNodeIds,
    },
  }
}

// Re-export types for convenience
export type { WorkflowValidationResult, ValidationIssue, NodeValidationState }
export { ValidationSeverity, ValidationCategory, ValidationErrorCodes } from '@features/adventures/types/validation.types'
